/**
 * Driver employment-modal gate (#359 / jobs 15920979476 / 15920979479).
 *
 * The "Adicionar Informações da Empresa" (EmploymentModal) belongs to the
 * COMPANY-funded onboarding path. A self-pay driver is entitled by their OWN
 * license (WS7 / #207), so the employment they declared is purely informative
 * and must NEVER trigger that modal.
 *
 * The gate consumes the tri-state self-pay entitlement (boolean | null):
 *   - null  : entitlement not yet resolved (hydration)  -> false, never open.
 *             This is the blocker: while hydrating we cannot yet tell a
 *             self-pay driver from a company-funded one, so the modal must stay
 *             closed so the dashboard is not covered.
 *   - true  : self-pay, resolved                      -> false (company path off).
 *   - false : company-funded, conclusive              -> open only when there is
 *             no active employment AND no employments at all.
 */

export interface EmploymentModalGateInput {
  hasActiveEmployment: boolean | null;
  employments: ReadonlyArray<unknown>;
  hasSelfPayLicense: boolean | null;
}

/**
 * @returns true ONLY for a conclusively company-funded driver with no active
 *          employment and no employments at all. Never while the entitlement is
 *          unknown (null) or for a self-pay driver.
 */
export function shouldShowEmploymentModal({
  hasActiveEmployment,
  employments,
  hasSelfPayLicense,
}: EmploymentModalGateInput): boolean {
  // Entitlement not yet resolved: never open the modal during hydration — we
  // cannot yet distinguish a self-pay driver from a company-funded one.
  if (hasSelfPayLicense === null) return false;
  // A self-pay driver is entitled by their own license (WS7 / #207): the
  // "Adicionar Informações da Empresa" modal is the company-funded path and
  // must stay closed.
  if (hasSelfPayLicense) return false;
  // Company-funded, conclusive: open only when there is no active employment
  // and no employments at all.
  return hasActiveEmployment === false && employments.length === 0;
}
